import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Spinner } from 'react-bootstrap';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { chatActions } from '../../actions/chat/chatActions.js';

const ConversationContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ConversationHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f9fb;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667eea;
  transition: all 0.2s ease;

  &:hover {
    color: #764ba2;
    transform: scale(1.1);
  }
`;

const ConversationTitle = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 14px;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;  display: flex;
  align-items: center;
  gap: 8px;
`;

const AnnouncementHeaderBadge = styled.span`
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  color: white;
  font-size: 10px;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;`;

const MessagesList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 3px;
  }
`;

const MessageBubble = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
  align-self: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
  gap: 2px;
  width: auto;
  max-width: 85%;
`;

const MessageBubbleContent = styled.div`
  background: ${props => props.isOwn ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e8eef5'};
  color: ${props => props.isOwn ? 'white' : '#333'};
  padding: 10px 14px;
  border-radius: 12px;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.4;
`;

const MessageTime = styled.div`
  font-size: 11px;
  color: #999;
  padding: 0 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ReadIndicator = styled.span`
  font-size: 10px;
  color: #667eea;
  font-weight: bold;
  ${props => props.isOwn ? '' : 'display: none;'}
`;

const ReadCountContainer = styled.div`
  font-size: 10px;
  color: #999;
  padding: 2px 4px;
  font-style: italic;
  position: relative;
  cursor: ${props => props.hasReaders ? 'help' : 'default'};
`;

const ReadTooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  background: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 11px;
  white-space: nowrap;
  z-index: 1000;
  margin-bottom: 4px;
  pointer-events: none;
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 4px;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid #333;
  }
`;

const ReaderList = styled.div`
  max-height: 150px;
  overflow-y: auto;
`;

const ReaderItem = styled.div`
  padding: 2px 0;
`;

const InputContainer = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 8px;
  background: #f8f9fb;
`;

const ReadOnlyNotice = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff3cd;
  color: #856404;
  font-size: 13px;
  font-weight: 500;
`;

const MessageInput = styled.input`
  flex: 1;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 10px 16px;
  font-size: 13px;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    background: #f0f0f0;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  gap: 12px;
`;

function ChatConversation(props) {
  const { 
    conversationId, 
    onBack,
    onUserTyping,
    onInputFocus,
    onInputBlur,
    messages,
    conversations,
    fetching,
    user,
    sendMessage,
    getMessages
  } = props;

  const [messageText, setMessageText] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [tooltipVisibleForMessage, setTooltipVisibleForMessage] = useState(null);
  const inputRef = useRef(null);
  const messagesListRef = useRef(null);
  const wasFetchingRef = useRef(false);

  const currentMessages = messages[conversationId] || [];
  const conversation = conversations.find(c => c.id === conversationId);

  // Scroll to bottom whenever messages change or after fetching completes
  useEffect(() => {
    if (messagesListRef.current && currentMessages.length > 0) {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }
  }, [currentMessages, conversationId]);

  // Also scroll when fetching completes (for polling scenarios)
  useEffect(() => {
    if (wasFetchingRef.current && !fetching.messages && currentMessages.length > 0) {
      if (messagesListRef.current) {
        messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
      }
    }
    wasFetchingRef.current = fetching.messages;
  }, [fetching.messages, currentMessages.length]);

  const handleSendMessage = useCallback(() => {
    if (messageText.trim()) {
      sendMessage(conversationId, messageText);
      setMessageText('');
    }
  }, [messageText, conversationId, sendMessage]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const handleInputChange = useCallback((e) => {
    setMessageText(e.target.value);
    // Notify parent that user is typing
    if (onUserTyping) {
      onUserTyping();
    }
  }, [onUserTyping]);

  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
    if (onInputFocus) {
      onInputFocus();
    }
  }, [onInputFocus]);

  const handleInputBlur = useCallback(() => {
    setIsInputFocused(false);
    if (onInputBlur) {
      onInputBlur();
    }
  }, [onInputBlur]);

  if (fetching.messages) {
    return (
      <ConversationContainer>
        <ConversationHeader>
          <BackButton onClick={onBack}>
            <ArrowBackIcon fontSize="small" />
          </BackButton>
          <ConversationTitle>{conversation?.participantName || conversation?.name || 'Loading...'}</ConversationTitle>
        </ConversationHeader>
        <EmptyState>
          <Spinner animation="border" variant="secondary" size="sm" />
        </EmptyState>
      </ConversationContainer>
    );
  }

  return (
    <ConversationContainer>
      <ConversationHeader>
        <BackButton onClick={onBack}>
          <ArrowBackIcon fontSize="small" />
        </BackButton>
        <ConversationTitle>
          {conversation?.isAnnouncementChannel && <AnnouncementHeaderBadge>📢 ANNOUNCEMENTS</AnnouncementHeaderBadge>}
          {conversation?.participantName || conversation?.name || 'Chat'}
        </ConversationTitle>
      </ConversationHeader>

      <MessagesList ref={messagesListRef}>
        {currentMessages.length === 0 ? (
          <EmptyState>
            <span>No messages yet</span>
            <small>Start the conversation</small>
          </EmptyState>
        ) : (
          currentMessages.map((message, index) => (
            <MessageBubble key={message.uuid || index} isOwn={message.isOwn}>
              <MessageBubbleContent isOwn={message.isOwn}>
                {message.message}
              </MessageBubbleContent>
              <MessageTime>
                {new Date(message.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                {message.isOwn && (
                  <>
                    <ReadIndicator isOwn={message.isOwn}>
                      {message.isRead ? '✓✓' : '✓'}
                    </ReadIndicator>
                    {message.readCount > 0 && (
                      <ReadCountContainer 
                        hasReaders={message.readByUsers && message.readByUsers.length > 0}
                        onMouseEnter={() => setTooltipVisibleForMessage(message.uuid || index)}
                        onMouseLeave={() => setTooltipVisibleForMessage(null)}
                      >
                        Read by {message.readCount} {message.readCount === 1 ? 'person' : 'people'}
                        {message.readByUsers && message.readByUsers.length > 0 && (
                          <ReadTooltip visible={tooltipVisibleForMessage === (message.uuid || index)}>
                            <ReaderList>
                              {message.readByUsers.map((reader, idx) => (
                                <ReaderItem key={idx}>
                                  {reader.name || reader.id}
                                </ReaderItem>
                              ))}
                            </ReaderList>
                          </ReadTooltip>
                        )}
                      </ReadCountContainer>
                    )}
                  </>
                )}
              </MessageTime>
            </MessageBubble>
          ))
        )}
      </MessagesList>
      {console.log(user)}

      {conversation?.isAnnouncementChannel && !user?.perm.some(e => e.role.id === 1)  ? (
        <ReadOnlyNotice>
          📢 This is an announcements board. Only admins can post here.
        </ReadOnlyNotice>
      ) : (
        <InputContainer>
          <MessageInput
            ref={inputRef}
            type="text"
            placeholder={conversation?.isAnnouncementChannel ? "Post an announcement..." : "Type a message..."}
            value={messageText}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            disabled={fetching.sendMessage}
          />
          <SendButton 
            onClick={handleSendMessage}
            disabled={!messageText.trim() || fetching.sendMessage}
          >
            {fetching.sendMessage ? (
              <Spinner animation="border" size="sm" style={{ color: 'white' }} />
            ) : (
              <SendIcon fontSize="small" />
            )}
          </SendButton>
        </InputContainer>
      )}
    </ConversationContainer>
  );
}

const mapStateToProps = (state) => {
  return {
    messages: state.chat.messages,
    conversations: state.chat.conversations,
    fetching: state.chat.fetching,
    user: state.users.user.user
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    sendMessage: (conversationId, message) => dispatch(chatActions.sendMessage(conversationId, message)),
    getMessages: (conversationId) => dispatch(chatActions.getMessages(conversationId))
  };
};

// Custom comparison for React.memo to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
  // Only re-render if these specific props change (ignore dispatch functions which always change)
  return (
    prevProps.conversationId === nextProps.conversationId &&
    prevProps.messages === nextProps.messages &&
    prevProps.conversations === nextProps.conversations &&
    prevProps.fetching === nextProps.fetching &&
    prevProps.user === nextProps.user &&
    prevProps.onInputFocus === nextProps.onInputFocus &&
    prevProps.onInputBlur === nextProps.onInputBlur
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(ChatConversation, arePropsEqual));
